import React, { useEffect, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { MocRow } from "../../../data/moc.data";
import { useGetMocHistoryQuery } from "../../../data/api/queries/getMocs";
import { MocHistoryItem } from "../../../data/models/moc.model";
import "./MocDetailDrawer.scss";

interface MocDetailDrawerProps {
  row: MocRow;
  onClose: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  REJECTED: "Rejected",
  PENDING: "Pending"
};

const formatStatusLabel = (status?: string): string => {
  if (!status) {
    return "";
  }
  const key = status.toUpperCase();
  return (
    STATUS_LABELS[key] ??
    status
      .toLowerCase()
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
};

type StageVisual = {
  color: string;
  Icon: React.ComponentType<{ fontSize?: "inherit" | "small" | "large" }>;
};

// Maps a workflow stage to the coloured marker/icon shown in the timeline.
const getStageVisual = (stage: string, isPending: boolean): StageVisual => {
  if (isPending) {
    return { color: "#9ca3af", Icon: RadioButtonUncheckedIcon };
  }
  const value = (stage || "").toUpperCase();
  if (value.includes("CREATED")) {
    return { color: "#2563eb", Icon: AddIcon };
  }
  if (value.includes("DRAWING") || value.includes("UPLOAD")) {
    return { color: "#16a34a", Icon: UploadFileIcon };
  }
  if (value.includes("ANALYSIS")) {
    return { color: "#9333ea", Icon: AutoAwesomeIcon };
  }
  if (value.includes("ASSESSMENT")) {
    return { color: "#f97316", Icon: CheckIcon };
  }
  if (value.includes("RISK")) {
    return { color: "#ef4444", Icon: WarningAmberIcon };
  }
  if (value.includes("APPROVAL") || value.includes("COMPLETED")) {
    return { color: "#6b7280", Icon: FlagOutlinedIcon };
  }
  return { color: "#2563eb", Icon: CheckIcon };
};

const MocDetailDrawer: React.FC<MocDetailDrawerProps> = ({ row, onClose }) => {
  const { data, isLoading, isError } = useGetMocHistoryQuery(row.mocId);

  const historyItems = useMemo<MocHistoryItem[]>(
    () => data?.items ?? [],
    [data]
  );

  // Derive live header values from the history rather than hardcoding them.
  const currentStatus = useMemo<string>(() => {
    if (historyItems.length === 0) {
      return "";
    }
    return formatStatusLabel(historyItems[historyItems.length - 1].status);
  }, [historyItems]);

  const createdOn = useMemo<string>(() => {
    const created = historyItems.find((item) => item.stage === "CREATED");
    const source = created ?? historyItems[0];
    if (!source) {
      return "";
    }
    return [source.date, source.time].filter(Boolean).join(" ");
  }, [historyItems]);

  // Close on Escape for accessibility.
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const renderAuditTrail = () => {
    if (isLoading) {
      return <p className="moc-drawer__muted">Loading audit trail...</p>;
    }
    if (isError) {
      return (
        <p className="moc-drawer__muted">
          We couldn&apos;t load the audit trail. Please try again.
        </p>
      );
    }
    if (historyItems.length === 0) {
      return <p className="moc-drawer__muted">No history available yet.</p>;
    }
    return (
      <ul className="moc-timeline">
        {historyItems.map((item, index) => {
          const isLast = index === historyItems.length - 1;
          const isPending = item.status?.toUpperCase() === "PENDING";
          const visual = getStageVisual(item.stage, isPending);
          const StageIcon = visual.Icon;
          return (
            <li
              key={item.id}
              className={`moc-timeline__item${
                isPending ? " moc-timeline__item--pending" : ""
              }`}
            >
              <div className="moc-timeline__marker">
                <span
                  className="moc-timeline__icon"
                  style={{ backgroundColor: visual.color }}
                >
                  <StageIcon fontSize="inherit" />
                </span>
                {!isLast && <span className="moc-timeline__line" />}
              </div>
              <div className="moc-timeline__body">
                <div className="moc-timeline__row">
                  <span className="moc-timeline__title">{item.stageLabel}</span>
                  <span className="moc-timeline__time">
                    {isPending ? "Pending" : item.time}
                  </span>
                </div>
                <div className="moc-timeline__row">
                  <span className="moc-timeline__by">{item.actionByName}</span>
                  {!isPending && (
                    <span className="moc-timeline__date">{item.date}</span>
                  )}
                </div>
                {item.comments && (
                  <p className="moc-timeline__comment">{item.comments}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="moc-drawer" role="dialog" aria-modal="true">
      <div className="moc-drawer__backdrop" onClick={onClose} />
      <aside className="moc-drawer__panel">
        <header className="moc-drawer__header">
          <h2 className="moc-drawer__heading">Engineering Review</h2>
          <button
            type="button"
            className="moc-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </header>

        <div className="moc-drawer__body">
          <section className="moc-drawer__summary">
            <div className="moc-drawer__summary-top">
              <div className="moc-drawer__id-block">
                <span className="moc-drawer__moc-id">{row.mocId}</span>
                <span className="moc-drawer__moc-title">{row.title}</span>
              </div>
              <div className="moc-drawer__summary-actions">
                {row.priority && (
                  <span
                    className={`moc-drawer__priority moc-drawer__priority--${row.priority.toLowerCase()}`}
                  >
                    {row.priority} PRIORITY
                  </span>
                )}
              </div>
            </div>

            <div className="moc-drawer__status-line">
              {currentStatus && (
                <span className="moc-drawer__status">
                  <span className="moc-drawer__status-dot" />
                  {currentStatus}
                </span>
              )}
              {row.stage && (
                <span className="moc-drawer__stage">{row.stage}</span>
              )}
            </div>

            <div className="moc-drawer__meta">
              <div className="moc-drawer__meta-item">
                <span className="moc-drawer__meta-label">ASSET</span>
                <span className="moc-drawer__meta-value">
                  {row.asset || "—"}
                </span>
              </div>
              <div className="moc-drawer__meta-item">
                <span className="moc-drawer__meta-label">ENGINEER</span>
                <span className="moc-drawer__meta-value">
                  {row.owner || "—"}
                </span>
              </div>
            </div>

            {createdOn && (
              <div className="moc-drawer__meta-item moc-drawer__meta-item--created">
                <span className="moc-drawer__meta-label">CREATED</span>
                <span className="moc-drawer__meta-value">{createdOn}</span>
              </div>
            )}
          </section>

          <nav className="moc-drawer__tabs">
            <button
              type="button"
              className="moc-drawer__tab moc-drawer__tab--active"
            >
              Audit Trail
            </button>
          </nav>

          <div className="moc-drawer__content">
            <div className="moc-drawer__content-header">
              <h3 className="moc-drawer__content-title">Audit Trail</h3>
            </div>
            {renderAuditTrail()}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default MocDetailDrawer;
